import { useMemo } from "react";
import { SortingState } from "@tanstack/react-table";
import { Button } from "@/components/Base/Button";
import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import { DataTable } from "@/components/Base/Datatable";
import { Card } from "@/types/v2/Card";
import { getJobRowsForEmployee } from "@/lib/dispatchJobRows";
import { sumHoursByState } from "@/lib/jobState";
import { useTableColumnVisibility } from "@/hooks/useTableColumnVisibility";
import { LOCAL_STORAGE_BASE_KEYS } from "@/types/v2";
import { employeeJobsColumns } from "./EmployeeJobsDetail.columns";
import { JobStateSummary } from "./JobStateSummary";
import { DISPATCH_TABLE_CLASSNAMES } from "./tableDensity";

const columns = employeeJobsColumns();
const DEFAULT_SORTING: SortingState = [{ id: "hours", desc: true }];

export function EmployeeJobsDetail({
  cards,
  employeeId,
  employeeName,
  onBack,
}: {
  cards: Card[];
  employeeId: string;
  employeeName: string;
  onBack: () => void;
}) {
  const jobRows = useMemo(
    () => getJobRowsForEmployee(cards, employeeId),
    [cards, employeeId]
  );

  const stateHours = useMemo(() => sumHoursByState(jobRows), [jobRows]);

  const { initialColumnVisibility, persistColumnVisibility } =
    useTableColumnVisibility(
      LOCAL_STORAGE_BASE_KEYS.DISPATCH_EMPLOYEE_JOBS_COLUMNS
    );

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-y-auto bg-slate-100 p-4">
      <div className="flex items-center gap-4">
        {/* ghost-alt (CardDetailsView.tsx's precedent) rests at
            text-primary-foreground, near-white — meant for a dark shell.
            This panel has an explicit light bg-slate-100, so use ghost
            (text-accent) instead, matching TasksTab.tsx's Button in this
            same sidebar. */}
        <Button variant="ghost" size="sm" onClick={onBack}>
          <Icon name={IconNames.leftArrow} size={16} />
          Back
        </Button>
        <h2 className="text-base font-bold text-[#0F172A]">{employeeName}</h2>
      </div>

      {jobRows.length > 0 && <JobStateSummary hours={stateHours} />}

      {jobRows.length === 0 ? (
        // The roster fix means every employee gets a row now, including
        // service advisors and other non-technician staff with no jobs at
        // all — this is a common path, not an edge case. Render our own
        // message rather than falling through to DataTable's generic
        // "No results." empty state, which reads like a filtered-out
        // search result rather than "this employee has nothing assigned."
        <div className="rounded-[10px] border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 shadow-[0_1px_2px_#0000000A]">
          No jobs assigned.
        </div>
      ) : (
        // Card drawn by containerClassName around the table alone, so the
        // Columns toolbar sits on the panel background (see tableDensity.ts).
        <DataTable
          columns={columns}
          data={jobRows}
          userDefaults={{
            sorting: DEFAULT_SORTING,
            columnVisibility: initialColumnVisibility,
          }}
          onColumnVisibilityChange={persistColumnVisibility}
          hidePagination
          containerClassName={DISPATCH_TABLE_CLASSNAMES.container}
          toolbarClassName={DISPATCH_TABLE_CLASSNAMES.toolbar}
        />
      )}
    </div>
  );
}
